import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next"; // اضافه شد
import {
    AdvancedHeartIcon,
    AllergyIcon,
    BloodPressureIcon,
    BloodSugarIcon,
    BodyFatIcon,
    ChevronDownIcon,
    DailyActivityIcon,
    HealthAlertsIcon,
    HeartIcon,
    HormonalCycleIcon,
    HydrationIcon,
    MedicationRemindeIcon,
    NotifIcon,
    OxygenIcon,
    ReactionSpeedIcon,
    RythemIcon,
    SleepIcon,
    StressIcon,
} from "../icons/icons";
import OxigenGraph from "../icons/OxigenGraph";
import SectionHeader from "../SectionHeader";
import SleepQualityGraph from "./graphs/SleepQualityGraph";
import HeartRateGraph from "./graphs/HeartRateGraph";
import OxygenLevelGraph from "./graphs/OxygenLevelGraph";
// Import sub-components
import BodyFatGraph from "./graphs/BodyFatGraph";
import BloodSugarGraph from "./graphs/BloodSugarGraph";
import ReactionSpeedGraph from "./graphs/ReactionSpeedGraph";
import AdvancedHeartGraph from "./graphs/AdvancedHeartGraph";
import MedicationReminderGraph from "./graphs/MedicationReminderGraph";
import HealthAlertsGraph from "./graphs/HealthAlertsGraph";
import AllergySensitivityGraph from "./graphs/AllergySensitivityGraph";
import HormonalCycleGraph from "./graphs/HormonalCycleGraph";
import BloodPressureGraph from "./graphs/BloodPressureGraph";
import HydrationGraph from "./graphs/HydrationGraph";
// Import translations
import faTranslations from "./locales/HealthMonitoring-fa.json";
import enTranslations from "./locales/HealthMonitoring-en.json";
import arTranslations from "./locales/HealthMonitoring-ar.json";
import trTranslations from "./locales/HealthMonitoring-tr.json";
import ExpandableCard from "./ExpandableCard";

interface HealthFeature {
    id: string;
    title: string;
    value: string;
    unit: string;
    subtitle?: string;
    icon: React.FC<{ className?: string }>;
    iconClass: string;
    textClass: string;
    hasGraph: boolean;
    graphType?: string;
    tooltip?: string | React.ReactNode;
    expandable: boolean;
    hasBadge?: boolean;
}

const HealthMonitoringBento: React.FC = () => {
    const { t, i18n } = useTranslation("healthMonitoring");

    // Load translation resources
    useEffect(() => {
        const ns = "healthMonitoring";
        const resources = {
            fa: faTranslations,
            en: enTranslations,
            ar: arTranslations,
            tr: trTranslations,
        };

        Object.entries(resources).forEach(([lng, data]) => {
            if (!i18n.hasResourceBundle(lng, ns)) {
                i18n.addResourceBundle(lng, ns, data, true, true);
            }
        });
    }, [i18n]);

    const currentLang = i18n.language || "fa";
    const isRTL = ["fa", "ar"].includes(currentLang);

    const [hoveredCard, setHoveredCard] = useState<string | null>(null);
    const [isVisible, setIsVisible] = useState(false);
    const [openCards, setOpenCards] = useState<string[]>([]);
    const [showMore, setShowMore] = useState(false);
    const [isMobile, setIsMobile] = useState(false); // Add isMobile state

    const sectionRef = useRef<HTMLDivElement>(null);

    // Add resize listener
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => window.removeEventListener("resize", checkMobile);
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsVisible(true);
        }, 100);
        return () => clearTimeout(timer);
    }, []);

    const toggleCard = (id: string) => {
        setOpenCards((prev) => (prev.includes(id) ? prev.filter((cardId) => cardId !== id) : [...prev, id]));
    };

    // Define features with translations
    const features: HealthFeature[] = [
        {
            id: "sleep-quality",
            title: t("features.sleep-quality.title", "😴 کیفیت خواب"),
            unit: t("features.sleep-quality.unit", "خواب شما در فازهای مختلف تحلیل شد"),
            value: "",
            icon: SleepIcon,
            iconClass: "bg-purple-900/30 backdrop-blur-sm rounded-full w-12 h-12 flex items-center justify-center mx-4",
            textClass: "text-purple-400",
            hasGraph: true,
            graphType: "sleep",
            tooltip: t("features.sleep-quality.tooltip", ""),
            expandable: false,
        },
        {
            id: "ecg-heart-rate",
            title: t("features.ecg-heart-rate.title", "🫀 نوار قلب و ضربان قلب"),
            unit: t("features.ecg-heart-rate.unit", "ضربان قلب شما در محدوده نرمال سن و فعالیت شماست."),
            value: "",
            icon: HeartIcon,
            iconClass: "bg-pink-900/30 backdrop-blur-sm rounded-full w-12 h-12 flex items-center justify-center mr-4",
            textClass: "text-pink-400",
            hasGraph: true,
            graphType: "heart",
            tooltip: t("features.ecg-heart-rate.tooltip", ""),
            expandable: false,
        },
        {
            id: "oxygen-level",
            title: t("features.oxygen-level.title", "🌬️ سطح اکسیژن"),
            unit: t("features.oxygen-level.unit", "سطح اکسیژن خون شما عالی و پایدار است."),
            value: "",
            icon: OxygenIcon,
            iconClass: "bg-blue-900/30 backdrop-blur-sm rounded-full w-12 h-12 flex items-center justify-center mr-4",
            textClass: "text-blue-400",
            hasGraph: true,
            graphType: "oxygen",
            tooltip: t("features.oxygen-level.tooltip", ""),
            expandable: false,
        },
        {
            id: "daily-activity",
            title: t("features.daily-activity.title", "🚶فعالیت روزانه"),
            value: t("features.daily-activity.value", "۸,۲۶۸"),
            unit: t("features.daily-activity.unit", "گام"),
            subtitle: t("features.daily-activity.subtitle", "۳.۸ مایل"),
            icon: DailyActivityIcon,
            iconClass: "bg-green-900/30 backdrop-blur-sm rounded-full w-12 h-12 flex items-center justify-center mr-4",
            textClass: "text-green-400",
            hasGraph: true,
            graphType: "steps",
            tooltip: t("features.daily-activity.tooltip", "تعداد گام‌های روزانه، مسافت طی شده، کالری مصرفی و دقایق فعال خود را با اهداف شخصی پیگیری کنید."),
            expandable: true,
        },
        {
            id: "stress-management",
            title: t("features.stress-management.title", "😌 مدیریت استرس"),
            value: t("features.stress-management.value", "کم"),
            unit: t("features.stress-management.unit", "۲۴ ساعت اخیر"),
            subtitle: t("features.stress-management.subtitle", "۲ ساعت مدیتیشن"),
            icon: StressIcon,
            iconClass: "bg-blue-900/30 backdrop-blur-sm rounded-full w-12 h-12 flex items-center justify-center mr-4",
            textClass: "text-blue-400",
            hasGraph: true,
            graphType: "stress",
            tooltip: t("features.stress-management.tooltip", "سطح استرس خود را با تحلیل تغییرپذیری ضربان قلب پیگیری کنید و در صورت افزایش استرس، تمرینات تنفس راهنما دریافت کنید."),
            expandable: true,
        },
        {
            id: "body-fat",
            title: t("features.body-fat.title", "🧬 چربی بدن"),
            value: t("features.body-fat.value", "۲۱.۳"),
            unit: t("features.body-fat.unit", "%"),
            subtitle: t("features.body-fat.subtitle", "محدوده سالم"),
            icon: BodyFatIcon,
            iconClass: "bg-emerald-900/30 backdrop-blur-sm rounded-full w-12 h-12 flex items-center justify-center mr-4",
            textClass: "text-emerald-400",
            hasGraph: false,
            tooltip: t("features.body-fat.tooltip", "شاخص‌های ترکیب بدن شامل درصد چربی بدن، توده عضلانی و سطح آب بدن را با آنالیز امپدانس زیستی پیگیری کنید."),
            expandable: true,
        },
        {
            id: "blood-sugar",
            title: t("features.blood-sugar.title", "🍬 قند خون"),
            value: t("features.blood-sugar.value", "۵.۴"),
            unit: t("features.blood-sugar.unit", "mmol/L"),
            subtitle: t("features.blood-sugar.subtitle", "محدوده نرمال"),
            icon: BloodSugarIcon,
            iconClass: "bg-red-900/30 backdrop-blur-sm rounded-full w-12 h-12 flex items-center justify-center mr-4",
            textClass: "text-red-400",
            hasGraph: true,
            graphType: "glucose",
            tooltip: t("features.blood-sugar.tooltip", "سطح قند خون خود را برای مدیریت دیابت، درک تاثیر غذا و حفظ انرژی مطلوب در طول روز پیگیری کنید."),
            expandable: true,
        },
        {
            id: "reaction-speed",
            title: t("features.reaction-speed.title", "⚡ سرعت واکنش"),
            value: t("features.reaction-speed.value", "۲۱۳"),
            unit: t("features.reaction-speed.unit", "ms"),
            subtitle: t("features.reaction-speed.subtitle", "عالی"),
            icon: ReactionSpeedIcon,
            iconClass: "bg-yellow-900/30 backdrop-blur-sm rounded-full w-12 h-12 flex items-center justify-center mr-4",
            textClass: "text-yellow-400",
            hasGraph: false,
            tooltip: t("features.reaction-speed.tooltip", "زمان واکنش شناختی و سلامت عصبی خود را با تست‌های تعاملی که روند پیشرفت را پیگیری می‌کنند، اندازه‌گیری کنید."),
            expandable: true,
        },
        {
            id: "advanced-heart",
            title: t("features.advanced-heart.title", "❤️ قلب پیشرفته"),
            value: t("features.advanced-heart.value", "۹۶%"),
            unit: t("features.advanced-heart.unit", "دقت"),
            subtitle: t("features.advanced-heart.subtitle", "تشخیص آریتمی"),
            icon: AdvancedHeartIcon,
            iconClass: "bg-violet-900/30 backdrop-blur-sm rounded-full w-12 h-12 flex items-center justify-center mx-4",
            textClass: "text-violet-400",
            hasGraph: true,
            graphType: "heart-advanced",
            tooltip: t("features.advanced-heart.tooltip", "تحلیل جامع سلامت قلب شامل HRV، تشخیص آریتمی و هشدارهای زودهنگام مشکلات قلبی عروقی."),
            expandable: true,
        },
        {
            id: "medication-reminder",
            title: t("features.medication-reminder.title", "💊 یادآور دارو"),
            value: t("features.medication-reminder.value", "۲"),
            unit: t("features.medication-reminder.unit", "دوز باقیمانده امروز"),
            subtitle: t("features.medication-reminder.subtitle", "آخرین دوز: ۴ ساعت پیش"),
            icon: MedicationRemindeIcon,
            iconClass: "bg-cyan-900/30 backdrop-blur-sm rounded-full w-12 h-12 flex items-center justify-center mr-4",
            textClass: "text-cyan-400",
            hasGraph: false,
            tooltip: t("features.medication-reminder.tooltip", ""),
            expandable: true,
        },
        {
            id: "health-alerts",
            title: t("features.health-alerts.title", "⚠️ هشدارهای سلامتی"),
            value: t("features.health-alerts.value", "۱"),
            unit: t("features.health-alerts.unit", "هشدار فعال"),
            subtitle: t("features.health-alerts.subtitle", "ضربان قلب نامنظم شناسایی شد"),
            icon: HealthAlertsIcon,
            iconClass: "bg-sky-900/30 backdrop-blur-sm rounded-full w-12 h-12 flex items-center justify-center mr-4",
            textClass: "text-sky-400",
            hasGraph: true,
            graphType: "alerts",
            tooltip: t("features.health-alerts.tooltip", ""),
            expandable: true,
            hasBadge: true,
        },
        {
            id: "hormonal-cycle",
            title: t("features.hormonal-cycle.title", "🌙 چرخه هورمونی"),
            value: t("features.hormonal-cycle.value", "۱۴"),
            unit: t("features.hormonal-cycle.unit", "روز چرخه"),
            subtitle: t("features.hormonal-cycle.subtitle", "فاز تخمک‌گذاری"),
            icon: HormonalCycleIcon,
            iconClass: "bg-fuchsia-900/30 backdrop-blur-sm rounded-full w-12 h-12 flex items-center justify-center mr-4",
            textClass: "text-fuchsia-400",
            hasGraph: true,
            graphType: "cycle",
            tooltip: t("features.hormonal-cycle.tooltip", ""),
            expandable: true,
            hasBadge: true,
        },
        {
            id: "blood-pressure",
            title: t("features.blood-pressure.title", "🩸 فشار خون"),
            value: t("features.blood-pressure.value", "۱۲۰/۸۰"),
            unit: t("features.blood-pressure.unit", "mmHg"),
            subtitle: t("features.blood-pressure.subtitle", "محدوده نرمال"),
            icon: BloodPressureIcon,
            iconClass: "bg-rose-900/30 backdrop-blur-sm rounded-full w-12 h-12 flex items-center justify-center mr-4",
            textClass: "text-rose-400",
            hasGraph: true,
            graphType: "blood-pressure",
            tooltip: t("features.blood-pressure.tooltip", ""),
            expandable: true,
            hasBadge: true,
        },
        {
            id: "hydration",
            title: t("features.hydration.title", "💧 هیدراتاسیون"),
            value: t("features.hydration.value", "۸۵٪"),
            unit: t("features.hydration.unit", "سطح آب بدن"),
            subtitle: t("features.hydration.subtitle", "۱.۸ لیتر مصرف شده"),
            icon: HydrationIcon,
            iconClass: "bg-sky-900/30 backdrop-blur-sm rounded-full w-12 h-12 flex items-center justify-center mr-4",
            textClass: "text-sky-400",
            hasGraph: true,
            graphType: "hydration",
            tooltip: t("features.hydration.tooltip", ""),
            expandable: true,
            hasBadge: true,
        },
        {
            id: "allergy-sensitivity",
            title: t("features.allergy-sensitivity.title", "🌿 آلرژی و حساسیت"),
            value: t("features.allergy-sensitivity.value", "کم"),
            unit: t("features.allergy-sensitivity.unit", "خطر امروز"),
            subtitle: t("features.allergy-sensitivity.subtitle", "گرده‌افشانی: متوسط"),
            icon: AllergyIcon,
            iconClass: "bg-lime-900/30 backdrop-blur-sm rounded-full w-12 h-12 flex items-center justify-center mr-4",
            textClass: "text-lime-400",
            hasGraph: false,
            tooltip: t("features.allergy-sensitivity.tooltip", ""),
            expandable: true,
        },
        {
            id: "readiness-score",
            title: t("features.readiness-score.title", "🎯 آمادگی روزانه"),
            value: t("features.readiness-score.value", "۸۵"),
            unit: t("features.readiness-score.unit", "%"),
            subtitle: t("features.readiness-score.subtitle", "زمان مناسبی برای چالش‌های جدید"),
            icon: DailyActivityIcon, // یا آیکن مخصوص
            iconClass: "bg-indigo-900/30 backdrop-blur-sm rounded-full w-12 h-12 flex items-center justify-center mr-4",
            textClass: "text-indigo-400",
            hasGraph: true,
            graphType: "readiness",
            tooltip: t("features.readiness-score.tooltip", "امتیاز آمادگی با ترکیب HRV، کیفیت خواب و فعالیت روز قبل محاسبه می‌شود."),
            expandable: true,
        },
        {
            id: "skin-temperature",
            title: t("features.skin-temperature.title"),
            value: t("features.skin-temperature.value"),
            unit: t("features.skin-temperature.unit"),
            subtitle: t("features.skin-temperature.subtitle"),
            icon: BodyFatIcon,
            iconClass: "bg-cyan-900/30 backdrop-blur-sm rounded-full w-12 h-12 flex items-center justify-center mr-4",
            textClass: "text-cyan-400",
            hasGraph: true,
            graphType: "temperature",
            tooltip: t("features.skin-temperature.tooltip"),
            expandable: true,
            hasBadge: true,
        },
        {
            id: "bio-age",
            title: t("features.bio-age.title"),
            value: t("features.bio-age.value"),
            unit: t("features.bio-age.unit"),
            subtitle: t("features.bio-age.subtitle"),
            icon: AdvancedHeartIcon,
            iconClass: "bg-purple-900/30 backdrop-blur-sm rounded-full w-12 h-12 flex items-center justify-center mr-4",
            textClass: "text-purple-400",
            hasGraph: true,
            graphType: "bio-age",
            tooltip: t("features.bio-age.tooltip"),
            expandable: true,
        },
        {
            id: "mood-tracking",
            title: t("features.mood-tracking.title"),
            value: t("features.mood-tracking.value"),
            unit: t("features.mood-tracking.unit"),
            subtitle: t("features.mood-tracking.subtitle"),
            icon: StressIcon,
            iconClass: "bg-yellow-900/30 backdrop-blur-sm rounded-full w-12 h-12 flex items-center justify-center mr-4",
            textClass: "text-yellow-400",
            hasGraph: true,
            graphType: "mood",
            tooltip: t("features.mood-tracking.tooltip"),
            expandable: true,
        },
        {
            id: "food-insights",
            title: t("features.food-insights.title"),
            value: t("features.food-insights.value"),
            unit: t("features.food-insights.unit"),
            subtitle: t("features.food-insights.subtitle"),
            icon: BloodSugarIcon,
            iconClass: "bg-green-900/30 backdrop-blur-sm rounded-full w-12 h-12 flex items-center justify-center mr-4",
            textClass: "text-green-400",
            hasGraph: true,
            graphType: "food",
            tooltip: t("features.food-insights.tooltip"),
            expandable: true,
        },
        {
            id: "workout-mode",
            title: t("features.workout-mode.title"),
            value: t("features.workout-mode.value"),
            unit: t("features.workout-mode.unit"),
            subtitle: t("features.workout-mode.subtitle"),
            icon: DailyActivityIcon,
            iconClass: "bg-blue-900/30 backdrop-blur-sm rounded-full w-12 h-12 flex items-center justify-center mr-4",
            textClass: "text-blue-400",
            hasGraph: true,
            graphType: "workout",
            tooltip: t("features.workout-mode.tooltip"),
            expandable: true,
        },
    ];

    return (
        <section id="monitoring" ref={sectionRef} dir={isRTL ? "rtl" : "ltr"}>
            <div className="container mx-auto px-4 sm:px-10">
                <motion.div className="text-center mb-6 relative" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, ease: [0.4, 0.0, 0.2, 1] }}>
                    <SectionHeader
                        regularText={t("header.title", "همه چیز درباره سلامتی، فقط در یک")}
                        highlightedText={t("header.titleHighlight", "نگاه")}
                        descriptionText={t("header.description", "از خواب تا ضربان قلب، داده‌ها همیشه همراه شما هستند تا تصمیم‌های بهتری برای سبک زندگی بگیرید.")}
                    />

                    <motion.div
                        className={`flex items-start justify-center gap-1.5 text-xs text-gray-400 max-w-2xl mx-auto mt-4 opacity-70`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.7 }}
                        transition={{ duration: 0.4, delay: 0.4 }}
                    >
                        <div className={`inline-flex items-start justify-center`}>
                            <svg className={`w-3.5 h-3.5 text-gray-600 ${isRTL ? "ml-1" : "mr-1"} mt-0.5`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-gray-600">{t("header.note", "نمایش هر ویجت بسته به نوع سنسور ساعت یا حلقه ممکن است متفاوت باشد.")}</span>
                        </div>
                    </motion.div>

                    <motion.div
                        className="absolute -z-10 inset-0 blur-3xl opacity-10"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.1 }}
                        transition={{ duration: 1 }}
                        style={{
                            background: "radial-gradient(circle at center, #8b5cf6, transparent 70%)",
                        }}
                    />
                </motion.div>

                <div className="py-5 rounded-2xl backdrop-blur-sm">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mx-auto">
                        {/* First 3 cards - Main graphs */}
                        {features.slice(0, 3).map((feature) => (
                            <motion.div
                                key={feature.id}
                                className="bg-white p-6 rounded-[24px] border border-[#E2E8F0] backdrop-blur-sm shadow-[0_10px_30px_rgba(0,0,0,0.05)] relative overflow-hidden group"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={isVisible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
                                transition={{
                                    duration: 0.2,
                                    delay: Math.random() * 0.2,
                                    ease: [0.4, 0.0, 0.2, 1],
                                }}
                                whileHover={{
                                    scale: 1.02,
                                    boxShadow: "0 12px 32px rgba(0,0,0,0.06)",
                                }}
                                onMouseEnter={() => setHoveredCard(feature.id)}
                                onMouseLeave={() => setHoveredCard(null)}
                            >
                                <motion.div
                                    className="absolute inset-0 -z-10"
                                    style={{
                                        background: feature.id.includes("oxygen")
                                            ? `radial-gradient(circle at 30% 20%, rgba(0, 229, 255, 0.1), transparent 70%)`
                                            : feature.id.includes("heart")
                                            ? `radial-gradient(circle at 30% 20%, rgba(0, 114, 206, 0.1), transparent 70%)`
                                            : `radial-gradient(circle at 30% 20%, rgba(167, 255, 235, 0.1), transparent 70%)`,
                                    }}
                                    animate={{ opacity: [0.1, 0.15, 0.1] }}
                                    transition={{
                                        duration: 2,
                                        repeat: Infinity,
                                        repeatType: "reverse",
                                        ease: [0.4, 0.0, 0.2, 1],
                                    }}
                                />

                                <div className={`flex items-center justify-between mb-4`}>
                                    <div className={`flex items-center flex-row`}>
                                        <div className={`bg-[#ECEFF1] rounded-[16px] p-3 flex items-center justify-center me-2 shadow-sm`}>
                                            <div className="text-[#0072CE]">
                                                <feature.icon />
                                            </div>
                                        </div>
                                        <div className={isRTL ? "text-right" : "text-left"}>
                                            <h3 className="font-medium text-[#002B5C] text-base">{feature.title}</h3>
                                            <div className={`flex items-baseline mt-1 `}>
                                                <span className={`text-2xl font-bold ${feature.textClass}`}>{feature.value}</span>
                                                <span className={`me-2 text-xs text-gray-400`}>{feature.unit}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {feature.subtitle && <div className={`text-sm text-gray-400 mb-4 text-start`}>{feature.subtitle}</div>}

                                <div className="w-full flex justify-center items-center mt-2">
                                    {feature.hasGraph && feature.graphType === "sleep" && <SleepQualityGraph />}
                                    {feature.hasGraph && feature.graphType === "heart" && <HeartRateGraph />}
                                    {feature.hasGraph && feature.graphType === "oxygen" && <OxygenLevelGraph />}
                                </div>
                            </motion.div>
                        ))}

                        {/* Cards 4-6 - Expandable (Desktop only) */}
                        {!isMobile &&
                            features.slice(3, 6).map((feature) => (
                                <motion.div
                                    key={feature.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{
                                        duration: 0.5,
                                        ease: [0.4, 0.0, 0.2, 1],
                                        delay: 0.1,
                                    }}
                                >
                                    <ExpandableCard feature={feature} isOpen={openCards.includes(feature.id)} onToggle={() => toggleCard(feature.id)} />
                                </motion.div>
                            ))}

                        {/* More cards - Shown when showMore is true */}
                        <AnimatePresence>
                            {showMore &&
                                features.slice(isMobile ? 3 : 6).map((feature, index) => ( // Slice from 3 on mobile, 6 on desktop
                                    <motion.div
                                        key={feature.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{
                                            opacity: 1,
                                            y: 0,
                                            transition: {
                                                duration: 0.5,
                                                ease: [0.4, 0.0, 0.2, 1],
                                                delay: index * 0.1,
                                            },
                                        }}
                                        exit={{
                                            opacity: 0,
                                            y: 20,
                                            transition: { duration: 0.3 },
                                        }}
                                    >
                                        <ExpandableCard feature={feature} isOpen={openCards.includes(feature.id)} onToggle={() => toggleCard(feature.id)} />
                                    </motion.div>
                                ))}
                        </AnimatePresence>
                    </div>

                    {/* Show More/Less Button */}
                    {!showMore ? (
                        <div className="flex justify-center mt-8">
                            <motion.button
                                className={`px-6 py-3 font-medium rounded-full bg-white/80 text-blue-600 border border-blue-200 hover:bg-blue-50/80 transition-all duration-300 flex items-center gap-2 backdrop-blur-sm shadow-sm ${
                                    isRTL ? "flex-row" : "flex-row-reverse"
                                }`}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => setShowMore(true)}
                            >
                                {t("buttons.showMore", "نمایش بیشتر")}
                                <motion.svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    animate={{ rotate: 0 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <polyline points="6 9 12 15 18 9"></polyline>
                                </motion.svg>
                            </motion.button>
                        </div>
                    ) : (
                        <div className="flex justify-center mt-8">
                            <motion.button
                                className={`px-6 py-3 rounded-full font-medium bg-white/80 text-blue-600 border border-blue-200 hover:bg-blue-50/80 transition-all duration-300 flex items-center gap-2 backdrop-blur-sm shadow-sm ${
                                    isRTL ? "flex-row-reverse" : "flex-row"
                                }`}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => setShowMore(false)}
                            >
                                {t("buttons.showLess", "نمایش کمتر")}
                                <motion.svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    animate={{ rotate: 180 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <polyline points="6 9 12 15 18 9"></polyline>
                                </motion.svg>
                            </motion.button>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default HealthMonitoringBento;